import { useEffect, useState } from 'react'
import { Download, QrCode } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import hubMark from '@/assets/hub-mark-qr.png'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  contactQrMatrix,
  qrLogoPlate,
  qrSvgPath,
  qrViewBoxSize,
  type QrMatrix,
} from '@/lib/inbox/contact-qr'

export interface ContactQrProps {
  contact: { name: string; vcard: string; downloadPath: string }
}

/**
 * "Add as contact" for the project's inbox address (PLAN-084): a popover
 * with a QR code that encodes the vCard itself — a phone camera offers to
 * save the contact straight away — and the same card as a `.vcf` download
 * for whoever is already on the device they want it on.
 */
export function ContactQr({ contact }: ContactQrProps) {
  const { t } = useTranslation('inbox')
  const [open, setOpen] = useState(false)
  const [encoded, setEncoded] = useState<{ vcard: string; matrix: QrMatrix } | null>(null)
  const matrix = encoded?.vcard === contact.vcard ? encoded.matrix : null

  // Encode lazily: the encoder is only loaded when someone opens the popover.
  useEffect(() => {
    if (!open || matrix) return
    let cancelled = false
    const vcard = contact.vcard
    void contactQrMatrix(vcard).then((m) => {
      if (!cancelled) setEncoded({ vcard, matrix: m })
    })
    return () => {
      cancelled = true
    }
  }, [open, matrix, contact.vcard])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground"
                aria-label={t('address.addContact')}
              >
                <QrCode className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('address.addContact')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('contact.title')}</p>
          <p className="text-xs text-muted-foreground">
            {t('contact.hint', { name: contact.name })}
          </p>
        </div>
        {/* Always black on white regardless of theme — cameras need the contrast. */}
        <div className="mx-auto flex aspect-square w-72 items-center justify-center rounded-md bg-white p-1">
          {matrix ? (
            <QrSymbol matrix={matrix} label={t('contact.qrAlt')} />
          ) : (
            <span className="text-xs text-neutral-500">{t('contact.loading')}</span>
          )}
        </div>
        <Button asChild variant="secondary" size="sm" className="w-full gap-1.5">
          <a href={contact.downloadPath} download>
            <Download className="h-4 w-4" />
            {t('contact.download')}
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  )
}

/** The mark is 570×640 (public/images/hub-mark.png); drawn this tall relative to the plate. */
const MARK_ASPECT = 570 / 640
const MARK_HEIGHT_SHARE = 0.68

/**
 * The symbol plus the HUB mark on a plate in the middle. The plate covers
 * ≤ 20 % of the width — about 4 % of the modules — against level H's 30 %
 * recovery; it is the app's card colour so it reads as part of the UI in
 * both themes (the mark is inverted on the dark card), the modules stay
 * black on white.
 */
function QrSymbol({ matrix, label }: { matrix: QrMatrix; label: string }) {
  const side = qrViewBoxSize(matrix)
  const plate = qrLogoPlate(matrix)
  const radius = plate.size * 0.18
  const markHeight = plate.size * MARK_HEIGHT_SHARE
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${side} ${side}`}
      className="h-full w-full"
      data-testid="contact-qr"
    >
      <path fill="#000" d={qrSvgPath(matrix)} shapeRendering="crispEdges" />
      <rect
        x={plate.x}
        y={plate.y}
        width={plate.size}
        height={plate.size}
        rx={radius}
        className="fill-card"
        data-testid="contact-qr-plate"
      />
      <image
        href={hubMark}
        x={plate.x + (plate.size - markHeight * MARK_ASPECT) / 2}
        y={plate.y + (plate.size - markHeight) / 2}
        width={markHeight * MARK_ASPECT}
        height={markHeight}
        className="dark:invert"
        data-testid="contact-qr-mark"
      />
    </svg>
  )
}
